import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import Canvas from './components/Canvas';
import QuickAddDialog from './components/QuickAddDialog';
import { FolderType, LinkType, ClipboardItem } from './types';
import { db } from './firebase';
import { collection, doc, writeBatch, getDocs } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { Plus } from 'lucide-react';
import { findFreePosition, snapToGrid } from './utils/collision';

const AppContent: React.FC = () => {
  const { user, logout } = useAuth();
  const [folders, setFolders] = useState<FolderType[]>([]);
  const [links, setLinks] = useState<LinkType[]>([]);
  const [loading, setLoading] = useState(true);

  // Undo/Redo history stack
  const [history, setHistory] = useState<{ folders: FolderType[]; links: LinkType[] }[]>([]);

  // Selection state
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Local clipboard for copy-paste
  const [clipboard, setClipboard] = useState<ClipboardItem | null>(null);

  // Save/Sync states
  const [syncStatus, setSyncStatus] = useState<'synced' | 'unsaved' | 'saving'>('synced');
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const createMenuRef = useRef<HTMLDivElement>(null);

  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load initial data from Firebase
  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const loadData = async () => {
      setLoading(true);

      // Timeout after 4 seconds to prevent hanging
      const timeout = setTimeout(() => {
        if (!cancelled) {
          setLoading(false);
        }
      }, 4000);

      try {
        const foldersCol = collection(db, 'folders');
        const linksCol = collection(db, 'links');

        const allFoldersSnap = await getDocs(foldersCol);
        const allLinksSnap = await getDocs(linksCol);

        const loadedFolders: FolderType[] = [];
        allFoldersSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          loadedFolders.push({
            id: docSnap.id,
            title: data.title || '',
            position: data.position || { x: 50, y: 50 },
            isPublic: !!data.isPublic,
            shareId: data.shareId || '',
            isPinned: !!data.isPinned,
            color: data.color || '#e2e8f0',
            richTextNote: data.richTextNote || '',
            comments: data.comments || [],
            tags: data.tags || [],
            status: data.status || 'active',
            linkIds: data.linkIds || [],
            createdBy: data.createdBy || '',
          });
        });

        const loadedLinks: LinkType[] = [];
        allLinksSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          loadedLinks.push({
            id: docSnap.id,
            title: data.title || '',
            url: data.url || '',
            description: data.description || '',
            favicon: data.favicon || '',
            tags: data.tags || [],
            status: data.status || 'pending',
            position: data.position || { x: 50, y: 50 },
            folderIds: data.folderIds || [],
            createdBy: data.createdBy || '',
            createdAt: data.createdAt || Date.now(),
          });
        });

        if (!cancelled) {
          setFolders(loadedFolders.map((f) => ({ ...f, position: snapToGrid(f.position.x, f.position.y) })));
          setLinks(loadedLinks);
          clearTimeout(timeout);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading workspace data:', err);
        if (!cancelled) {
          clearTimeout(timeout);
          setLoading(false);
        }
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [user]);

  // Synchronize/Save state with Firestore
  const syncWithFirebase = useCallback(async (currentFolders: FolderType[], currentLinks: LinkType[]) => {
    if (!user) return;
    setSyncStatus('saving');

    try {
      const batch = writeBatch(db);

      // Save/overwrite folders
      currentFolders.forEach((f) => {
        const fRef = doc(db, 'folders', f.id);
        batch.set(fRef, {
          title: f.title,
          position: f.position,
          isPublic: f.isPublic,
          shareId: f.shareId || null,
          isPinned: f.isPinned,
          color: f.color,
          richTextNote: f.richTextNote,
          comments: f.comments,
          tags: f.tags,
          status: f.status,
          linkIds: f.linkIds,
          createdBy: f.createdBy || user.uid,
        });
      });

      // Save/overwrite links
      currentLinks.forEach((l) => {
        const lRef = doc(db, 'links', l.id);
        batch.set(lRef, {
          title: l.title,
          url: l.url,
          description: l.description || '',
          favicon: l.favicon || null,
          tags: l.tags,
          status: l.status,
          position: l.position,
          folderIds: l.folderIds,
          createdBy: l.createdBy || user.uid,
          createdAt: l.createdAt,
        });
      });

      await batch.commit();
      setSyncStatus('synced');
    } catch (err: any) {
      console.error('Error saving data:', err);
      setSyncStatus('unsaved');
      // Show a user-friendly hint if the error looks like Firestore isn't set up
      if (err?.code === 'not-found' || err?.message?.includes('database')) {
        console.warn('Firestore database not found. Go to Firebase Console → Firestore → Create database.');
      }
    }
  }, [user]);

  // Trigger auto-save whenever folders or links are updated
  const triggerAutoSave = useCallback((newFolders: FolderType[], newLinks: LinkType[]) => {
    setSyncStatus('unsaved');
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      syncWithFirebase(newFolders, newLinks);
    }, 3000);
  }, [syncWithFirebase]);

  // Update states with history for Undo
  const pushToHistory = useCallback((prevFolders: FolderType[], prevLinks: LinkType[]) => {
    setHistory((prev) => {
      const updated = [...prev, { folders: prevFolders, links: prevLinks }];
      if (updated.length > 50) updated.shift(); // Keep history size to 50 items
      return updated;
    });
  }, []);

  const handleFoldersChange = useCallback((newFolders: FolderType[]) => {
    pushToHistory(folders, links);
    setFolders(newFolders);
    triggerAutoSave(newFolders, links);
  }, [folders, links, pushToHistory, triggerAutoSave]);

  const handleLinksChange = useCallback((newLinks: LinkType[]) => {
    pushToHistory(folders, links);
    setLinks(newLinks);
    triggerAutoSave(folders, newLinks);
  }, [folders, links, pushToHistory, triggerAutoSave]);

  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const prevHistory = [...history];
    const lastState = prevHistory.pop();
    if (lastState) {
      setFolders(lastState.folders);
      setLinks(lastState.links);
      setHistory(prevHistory);
      syncWithFirebase(lastState.folders, lastState.links);
    }
  }, [history, syncWithFirebase]);

  // Delete handlers
  const handleDeleteFolder = useCallback((folderId: string) => {
    pushToHistory(folders, links);
    const updatedFolders = folders.filter((f) => f.id !== folderId);
    // Remove reference from links
    const updatedLinks = links.map((l) => ({
      ...l,
      folderIds: l.folderIds.filter((id) => id !== folderId),
    }));
    setFolders(updatedFolders);
    setLinks(updatedLinks);
    triggerAutoSave(updatedFolders, updatedLinks);
    if (selectedId === folderId) setSelectedId(null);
  }, [folders, links, selectedId, pushToHistory, triggerAutoSave]);

  const handleDeleteLink = useCallback((linkId: string) => {
    pushToHistory(folders, links);
    const updatedLinks = links.filter((l) => l.id !== linkId);
    setLinks(updatedLinks);
    triggerAutoSave(folders, updatedLinks);
    if (selectedId === linkId) setSelectedId(null);
  }, [folders, links, selectedId, pushToHistory, triggerAutoSave]);

  // Clipboard copy and paste logic
  const handleCopy = useCallback(() => {
    if (!selectedId) return;
    const selectedFolder = folders.find((f) => f.id === selectedId);
    if (selectedFolder) {
      setClipboard({ type: 'folder', data: selectedFolder });
      return;
    }
    const selectedLink = links.find((l) => l.id === selectedId);
    if (selectedLink) {
      setClipboard({ type: 'link', data: selectedLink });
    }
  }, [selectedId, folders, links]);

  const handlePaste = useCallback(() => {
    if (!clipboard) return;
    pushToHistory(folders, links);

    const offset = 30;

    if (clipboard.type === 'folder') {
      const original = clipboard.data as FolderType;
      const basePos = { x: original.position.x + offset, y: original.position.y + offset };
      const pos = findFreePosition(basePos.x, basePos.y, '', folders);
      const newFolder: FolderType = {
        ...original,
        id: doc(collection(db, 'folders')).id,
        title: `${original.title} (העתק)`,
        position: { x: pos.x, y: pos.y },
        createdBy: user?.uid || '',
        comments: [], // Fresh comments for pasted folders
      };
      const updated = [...folders, newFolder];
      setFolders(updated);
      triggerAutoSave(updated, links);
    } else {
      const original = clipboard.data as LinkType;
      const newLink: LinkType = {
        ...original,
        id: doc(collection(db, 'links')).id,
        title: `${original.title} (העתק)`,
        position: { x: original.position.x + offset, y: original.position.y + offset },
        createdBy: user?.uid || '',
        createdAt: Date.now(),
      };
      const updated = [...links, newLink];
      setLinks(updated);
      triggerAutoSave(folders, updated);
    }
  }, [clipboard, folders, links, user, pushToHistory, triggerAutoSave]);

  const handleCreateFolder = useCallback(() => {
    const baseX = 100 + Math.random() * 200;
    const baseY = 100 + Math.random() * 200;
    const pos = findFreePosition(baseX, baseY, '', folders);
    const newFolder: FolderType = {
      id: uuidv4(),
      title: 'תיקייה חדשה',
      position: { x: pos.x, y: pos.y },
      isPublic: false,
      isPinned: false,
      color: '#e2e8f0',
      richTextNote: '',
      comments: [],
      tags: [],
      status: 'active',
      linkIds: [],
      createdBy: user?.uid || '',
    };
    pushToHistory(folders, links);
    const updated = [...folders, newFolder];
    setFolders(updated);
    triggerAutoSave(updated, links);
    setShowCreateMenu(false);
  }, [folders, links, user, pushToHistory, triggerAutoSave]);

  // Close create menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target as Node)) {
        setShowCreateMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Setup Global Keyboard Shortcut Event Listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCmdOrCtrl = e.ctrlKey || e.metaKey;

      if (isCmdOrCtrl && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        handleUndo();
      }

      if (isCmdOrCtrl && e.key.toLowerCase() === 's') {
        e.preventDefault();
        syncWithFirebase(folders, links);
      }

      if (isCmdOrCtrl && e.key.toLowerCase() === 'c') {
        // Only trigger copy if user is not focusing an input or textarea
        const tag = document.activeElement?.tagName.toLowerCase();
        if (tag !== 'input' && tag !== 'textarea' && !document.activeElement?.hasAttribute('contenteditable')) {
          e.preventDefault();
          handleCopy();
        }
      }

      if (isCmdOrCtrl && e.key.toLowerCase() === 'v') {
        const tag = document.activeElement?.tagName.toLowerCase();
        if (tag !== 'input' && tag !== 'textarea' && !document.activeElement?.hasAttribute('contenteditable')) {
          e.preventDefault();
          handlePaste();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, syncWithFirebase, handleCopy, handlePaste, folders, links]);

  if (!user) {
    return <Login />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 font-heebo" dir="rtl">
        <div className="text-sm font-black text-black tracking-widest border border-solid border-black px-6 py-3 bg-white">
          טוען נתונים...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white" dir="rtl">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 flex items-center justify-between p-4 border-b border-solid border-black bg-white select-none font-heebo">
        <div className="flex items-center gap-4">
          <span className="text-xl font-black text-black tracking-tight border-b-2 border-solid border-black pb-0.5">
            LINK2ME
          </span>
          <span className="text-xs text-gray-400 font-bold hidden md:inline">
            סביבת עבודה צוותית
          </span>
        </div>

        <div className="flex items-center gap-4">
          {/* Create Button with dropdown */}
          <div ref={createMenuRef} className="relative">
            <button
              onClick={() => setShowCreateMenu(!showCreateMenu)}
              className="flex items-center gap-1 px-3 py-1 bg-black text-white hover:bg-gray-900 border border-solid border-black text-xs font-bold transition-all cursor-pointer"
              title="צור פריט חדש (Ctrl+K)"
            >
              <Plus size={14} />
              <span>חדש</span>
            </button>
            {showCreateMenu && (
              <div className="absolute left-0 top-full mt-1 bg-white border border-solid border-black z-50 min-w-[140px] flex flex-col" style={{ direction: 'rtl' }}>
                <button
                  onClick={() => { handleCreateFolder(); }}
                  className="px-3 py-2 text-xs font-bold text-right text-black hover:bg-gray-100 border-b border-solid border-gray-200 cursor-pointer"
                >
                  תיקייה חדשה
                </button>
                <button
                  onClick={() => { setShowCreateMenu(false); setIsQuickAddOpen(true); }}
                  className="px-3 py-2 text-xs font-bold text-right text-black hover:bg-gray-100 cursor-pointer"
                >
                  לינק חדש
                </button>
              </div>
            )}
          </div>

          {/* Sync Status Badge */}
          <div className="flex items-center gap-1.5 text-xs font-bold">
            {syncStatus === 'synced' && (
              <span className="px-2.5 py-1 border border-solid border-green-500 bg-green-50 text-green-700">
                ● מסונכרן לענן
              </span>
            )}
            {syncStatus === 'unsaved' && (
              <span className="px-2.5 py-1 border border-solid border-yellow-500 bg-yellow-50 text-yellow-700 animate-pulse">
                ● יש שינויים לא שמורים
              </span>
            )}
            {syncStatus === 'saving' && (
              <span className="px-2.5 py-1 border border-solid border-blue-500 bg-blue-50 text-blue-700">
                ● מסתנכרן...
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (selectedId) {
                  const f = folders.find(x => x.id === selectedId);
                  if (f) { handleDeleteFolder(selectedId); return; }
                  const l = links.find(x => x.id === selectedId);
                  if (l) { handleDeleteLink(selectedId); }
                }
              }}
              disabled={!selectedId}
              className={`px-3 py-1 border border-solid border-red-500 text-xs font-bold transition-all rounded-none ${selectedId ? 'bg-red-50 text-red-700 hover:bg-red-100 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
              title="מחק פריט נבחר"
            >
              🗑️
            </button>
            <button
              onClick={() => syncWithFirebase(folders, links)}
              className="px-3 py-1 bg-black text-white hover:bg-gray-900 border border-solid border-black text-xs font-bold transition-all rounded-none cursor-pointer"
              title="שמור עכשיו (Ctrl+S)"
            >
              💾 שמור
            </button>
          </div>

          <div className="h-6 w-[1px] bg-gray-300" />

          {/* User Profile and Logout */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-black font-bold hidden sm:inline">
              {user.displayName || user.email}
            </span>
            <button
              onClick={logout}
              className="px-3 py-1 bg-black text-white hover:bg-gray-900 border border-solid border-black text-xs font-bold transition-all rounded-none cursor-pointer"
            >
              התנתק
            </button>
          </div>
        </div>
      </header>

      {/* Main interactive canvas area */}
      <main className="flex-1 relative">
        <Canvas
          folders={folders}
          links={links}
          onFoldersChange={handleFoldersChange}
          onLinksChange={handleLinksChange}
          onUndo={handleUndo}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onDeleteFolder={handleDeleteFolder}
          onDeleteLink={handleDeleteLink}
          onQuickAddOpen={() => setIsQuickAddOpen(true)}
          onCreateFolder={handleCreateFolder}
        />
      </main>

      {/* Quick Add Link popup dialog */}
      <QuickAddDialog
        isOpen={isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        onAddLink={(newLink) => {
          const updatedLinks = [...links, { ...newLink, createdBy: user.uid }];
          setLinks(updatedLinks);
          triggerAutoSave(folders, updatedLinks);
        }}
      />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;